/**
 * Box Plot Chart Component
 * Visualizes quartile statistics with outliers
 * Based on IECON2023 Paper Figure 9 (right side)
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Typography, Modal, IconButton, useTheme } from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';

interface BoxPlotChartProps {
  statistics: {
    Q1: number;
    Q2_median: number;
    Q3: number;
    IQR: number;  // Used for chart range calculations
    biggest_outlier: number;  // Displayed instead of IQR in UI
    lower_bound: number;
    upper_bound: number;
    min_power: number;
    max_power_peak: number;
    outlier_values?: number[];
  };
  title?: string;
  height?: number;
}

const BoxPlotChart: React.FC<BoxPlotChartProps> = ({
  statistics,
  title = 'Box Plot Analysis',
  height = 400
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Dynamic colors based on theme
  const colors = {
    background: isDark ? '#0d1117' : '#ffffff',
    text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    primary: isDark ? '#4a9eff' : '#2563eb',
    primaryLight: isDark ? 'rgba(74, 158, 255, 0.3)' : 'rgba(37, 99, 235, 0.3)',
    accent: isDark ? '#ffa726' : '#f97316',
    success: isDark ? '#66bb6a' : '#16a34a',
    danger: isDark ? '#ef5350' : '#dc2626'
  };

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !statistics) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { Q1, Q2_median, Q3, IQR, lower_bound, upper_bound, min_power, max_power_peak, outlier_values } = statistics;

    // Chart dimensions
    const padding = 80;
    const paddingBottom = 120; // Extra space for legend at bottom
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding - paddingBottom;

    // Data range
    const minValue = Math.min(min_power, lower_bound - IQR);
    const maxValue = Math.max(max_power_peak, upper_bound + IQR);
    const range = maxValue - minValue;

    // Clear canvas with theme-aware background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Helper function
    const yScale = (value: number) => padding + chartHeight - ((value - minValue) / range) * chartHeight;

    // Draw Y-axis (theme-aware)
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // Draw Y-axis ticks and labels (theme-aware)
    ctx.fillStyle = colors.text;
    ctx.font = '12px Arial';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const value = minValue + (i / 5) * range;
      const y = yScale(value);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(padding - 5, y);
      ctx.lineTo(padding + 5, y);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(value).toString(), padding - 10, y + 5);
    }

    // Y-axis label
    ctx.save();
    ctx.translate(20, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Power [W]', 0, 0);
    ctx.restore();

    // Box plot center X position
    const centerX = padding + chartWidth / 2;
    const boxWidth = 120;

    // Draw Whiskers (from lower_bound to upper_bound)
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 2;

    // Lower whisker
    ctx.beginPath();
    ctx.moveTo(centerX, yScale(lower_bound));
    ctx.lineTo(centerX, yScale(Q1));
    ctx.stroke();

    // Lower whisker cap
    ctx.beginPath();
    ctx.moveTo(centerX - 20, yScale(lower_bound));
    ctx.lineTo(centerX + 20, yScale(lower_bound));
    ctx.stroke();

    // Upper whisker
    ctx.beginPath();
    ctx.moveTo(centerX, yScale(Q3));
    ctx.lineTo(centerX, yScale(upper_bound));
    ctx.stroke();

    // Upper whisker cap
    ctx.beginPath();
    ctx.moveTo(centerX - 20, yScale(upper_bound));
    ctx.lineTo(centerX + 20, yScale(upper_bound));
    ctx.stroke();

    // Draw Box (Q1 to Q3)
    const boxY = yScale(Q3);
    const boxHeight = yScale(Q1) - yScale(Q3);

    ctx.fillStyle = colors.primaryLight;
    ctx.fillRect(centerX - boxWidth / 2, boxY, boxWidth, boxHeight);

    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.strokeRect(centerX - boxWidth / 2, boxY, boxWidth, boxHeight);

    // Draw Median line (Q2)
    ctx.strokeStyle = '#d32f2f';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(centerX - boxWidth / 2, yScale(Q2_median));
    ctx.lineTo(centerX + boxWidth / 2, yScale(Q2_median));
    ctx.stroke();

    // Draw Outliers as red "+" symbols
    if (outlier_values && outlier_values.length > 0) {
      ctx.strokeStyle = '#d32f2f';
      ctx.lineWidth = 2;

      outlier_values.forEach((value) => {
        // Only draw outliers that are outside the bounds
        if (value > upper_bound || value < lower_bound) {
          const y = yScale(value);
          const size = 6;

          // Draw "+" symbol
          // Horizontal line
          ctx.beginPath();
          ctx.moveTo(centerX - size, y);
          ctx.lineTo(centerX + size, y);
          ctx.stroke();

          // Vertical line
          ctx.beginPath();
          ctx.moveTo(centerX, y - size);
          ctx.lineTo(centerX, y + size);
          ctx.stroke();
        }
      });
    }

    // Draw legend - horizontal unter dem Chart
    const legendY = padding + chartHeight + 30;
    const legendItemWidth = 160;

    ctx.font = '12px Arial';
    ctx.textAlign = 'left';

    let legendItemX = padding;

    // Box
    ctx.fillStyle = colors.primaryLight;
    ctx.fillRect(legendItemX, legendY - 5, 20, 10);
    ctx.strokeStyle = colors.primary;
    ctx.lineWidth = 1;
    ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
    ctx.fillStyle = colors.text;
    ctx.fillText('Box (Q1-Q3)', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Median
    ctx.strokeStyle = colors.danger;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(legendItemX, legendY);
    ctx.lineTo(legendItemX + 20, legendY);
    ctx.stroke();
    ctx.fillStyle = colors.text;
    ctx.fillText('Median (Q2)', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Whiskers
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(legendItemX + 10, legendY - 5);
    ctx.lineTo(legendItemX + 10, legendY + 5);
    ctx.stroke();
    ctx.fillStyle = colors.text;
    ctx.fillText('Whiskers (1.5×IQR)', legendItemX + 25, legendY + 4);

    legendItemX += legendItemWidth;

    // Outliers
    const size = 6;
    ctx.strokeStyle = colors.danger;
    ctx.lineWidth = 2;

    // Draw "+" symbol
    ctx.beginPath();
    ctx.moveTo(legendItemX + 10 - size, legendY);
    ctx.lineTo(legendItemX + 10 + size, legendY);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(legendItemX + 10, legendY - size);
    ctx.lineTo(legendItemX + 10, legendY + size);
    ctx.stroke();

    ctx.fillStyle = colors.text;
    ctx.fillText('Outliers', legendItemX + 25, legendY + 4);

    // Draw labels for quartiles
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = colors.text;

    // Q1 label
    ctx.fillText(`Q1: ${Q1.toFixed(0)} W`, centerX - boxWidth / 2 - 40, yScale(Q1));

    // Q2 label
    ctx.fillText(`Q2: ${Q2_median.toFixed(0)} W`, centerX + boxWidth / 2 + 40, yScale(Q2_median));

    // Q3 label
    ctx.fillText(`Q3: ${Q3.toFixed(0)} W`, centerX - boxWidth / 2 - 40, yScale(Q3));

  }, [statistics, colors]);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  if (!statistics) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Keine Statistik-Daten verfügbar
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {title && (
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          IQR-basierte Ausreißererkennung
        </Typography>
        <Box sx={{ position: 'relative', cursor: 'zoom-in' }}>
          <canvas
            ref={canvasRef}
            width={800}
            height={height}
            style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
            onClick={() => setIsZoomed(true)}
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.95)' }
            }}
            size="small"
            onClick={() => setIsZoomed(true)}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Zoom Modal */}
      <Modal
        open={isZoomed}
        onClose={() => setIsZoomed(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            maxWidth: '95vw',
            maxHeight: '95vh',
            overflow: 'auto'
          }}
        >
          <IconButton
            onClick={() => setIsZoomed(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          {title && (
            <Typography variant="h6" fontWeight="600" gutterBottom>
              {title}
            </Typography>
          )}
          <canvas
            width={1200}
            height={height * 1.5}
            style={{ width: '100%', height: 'auto', cursor: 'zoom-out' }}
            onClick={() => setIsZoomed(false)}
            ref={(canvas) => {
              if (canvas && isZoomed && statistics) {
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                const { Q1, Q2_median, Q3, IQR, lower_bound, upper_bound, min_power, max_power_peak, outlier_values } = statistics;

                // Chart dimensions for zoomed view
                const padding = 120;
                const paddingBottom = 150;
                const chartWidth = canvas.width - padding * 2;
                const chartHeight = canvas.height - padding - paddingBottom;

                const minValue = Math.min(min_power, lower_bound - IQR);
                const maxValue = Math.max(max_power_peak, upper_bound + IQR);
                const range = maxValue - minValue;

                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                const yScale = (value: number) => padding + chartHeight - ((value - minValue) / range) * chartHeight;

                // Draw Y-axis
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(padding, padding);
                ctx.lineTo(padding, padding + chartHeight);
                ctx.stroke();

                // Draw Y-axis ticks and labels
                ctx.fillStyle = '#000';
                ctx.font = '16px Arial';
                ctx.textAlign = 'right';
                for (let i = 0; i <= 5; i++) {
                  const value = minValue + (i / 5) * range;
                  const y = yScale(value);
                  ctx.beginPath();
                  ctx.moveTo(padding - 5, y);
                  ctx.lineTo(padding + 5, y);
                  ctx.stroke();
                  ctx.fillText(Math.round(value).toString(), padding - 10, y + 5);
                }

                // Y-axis label
                ctx.save();
                ctx.translate(30, canvas.height / 2);
                ctx.rotate(-Math.PI / 2);
                ctx.font = '18px Arial';
                ctx.textAlign = 'center';
                ctx.fillText('Power [W]', 0, 0);
                ctx.restore();

                const centerX = padding + chartWidth / 2;
                const boxWidth = 150;

                // Draw Whiskers
                ctx.strokeStyle = '#666';
                ctx.lineWidth = 3;

                ctx.beginPath();
                ctx.moveTo(centerX, yScale(lower_bound));
                ctx.lineTo(centerX, yScale(Q1));
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(centerX - 25, yScale(lower_bound));
                ctx.lineTo(centerX + 25, yScale(lower_bound));
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(centerX, yScale(Q3));
                ctx.lineTo(centerX, yScale(upper_bound));
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(centerX - 25, yScale(upper_bound));
                ctx.lineTo(centerX + 25, yScale(upper_bound));
                ctx.stroke();

                // Draw Box
                const boxY = yScale(Q3);
                const boxHeight = yScale(Q1) - yScale(Q3);

                ctx.fillStyle = colors.primaryLight;
                ctx.fillRect(centerX - boxWidth / 2, boxY, boxWidth, boxHeight);

                ctx.strokeStyle = '#1976d2';
                ctx.lineWidth = 3;
                ctx.strokeRect(centerX - boxWidth / 2, boxY, boxWidth, boxHeight);

                // Draw Median line
                ctx.strokeStyle = '#d32f2f';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(centerX - boxWidth / 2, yScale(Q2_median));
                ctx.lineTo(centerX + boxWidth / 2, yScale(Q2_median));
                ctx.stroke();

                // Draw Outliers
                if (outlier_values && outlier_values.length > 0) {
                  ctx.strokeStyle = '#d32f2f';
                  ctx.lineWidth = 3;

                  outlier_values.forEach((value) => {
                    if (value > upper_bound || value < lower_bound) {
                      const y = yScale(value);
                      const size = 8;

                      ctx.beginPath();
                      ctx.moveTo(centerX - size, y);
                      ctx.lineTo(centerX + size, y);
                      ctx.stroke();

                      ctx.beginPath();
                      ctx.moveTo(centerX, y - size);
                      ctx.lineTo(centerX, y + size);
                      ctx.stroke();
                    }
                  });
                }

                // Draw legend
                const legendY = padding + chartHeight + 40;
                const legendItemWidth = 200;

                ctx.font = '16px Arial';
                ctx.textAlign = 'left';

                let legendItemX = padding;

                ctx.fillStyle = colors.primaryLight;
                ctx.fillRect(legendItemX, legendY - 5, 25, 12);
                ctx.strokeStyle = '#1976d2';
                ctx.lineWidth = 1;
                ctx.strokeRect(legendItemX, legendY - 5, 25, 12);
                ctx.fillStyle = '#000';
                ctx.fillText('Box (Q1-Q3)', legendItemX + 30, legendY + 6);

                legendItemX += legendItemWidth;

                ctx.strokeStyle = '#d32f2f';
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(legendItemX, legendY + 1);
                ctx.lineTo(legendItemX + 25, legendY + 1);
                ctx.stroke();
                ctx.fillStyle = '#000';
                ctx.fillText('Median (Q2)', legendItemX + 30, legendY + 6);

                legendItemX += legendItemWidth;

                ctx.strokeStyle = '#666';
                ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(legendItemX + 12, legendY - 5);
                ctx.lineTo(legendItemX + 12, legendY + 7);
                ctx.stroke();
                ctx.fillStyle = '#000';
                ctx.fillText('Whiskers (1.5×IQR)', legendItemX + 30, legendY + 6);

                legendItemX += legendItemWidth;

                const size = 8;
                ctx.strokeStyle = '#d32f2f';
                ctx.lineWidth = 3;

                ctx.beginPath();
                ctx.moveTo(legendItemX + 12 - size, legendY + 1);
                ctx.lineTo(legendItemX + 12 + size, legendY + 1);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(legendItemX + 12, legendY + 1 - size);
                ctx.lineTo(legendItemX + 12, legendY + 1 + size);
                ctx.stroke();

                ctx.fillStyle = '#000';
                ctx.fillText('Outliers', legendItemX + 30, legendY + 6);

                // Draw labels for quartiles
                ctx.font = '14px Arial';
                ctx.textAlign = 'center';
                ctx.fillStyle = '#333';

                ctx.fillText(`Q1: ${Q1.toFixed(0)} W`, centerX - boxWidth / 2 - 60, yScale(Q1));
                ctx.fillText(`Q2: ${Q2_median.toFixed(0)} W`, centerX + boxWidth / 2 + 60, yScale(Q2_median));
                ctx.fillText(`Q3: ${Q3.toFixed(0)} W`, centerX - boxWidth / 2 - 60, yScale(Q3));
              }
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default BoxPlotChart;
